import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Tabs, message } from 'antd';
import styled from '@emotion/styled';
import { CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DetailsTab, UsersTab } from './tab-pane';
import { SpinLoading } from '../../atoms';
import { postCreateOrUpdateTourTrash } from '../../../repository/mytm/tour-trash/tourTrashRepository';
import { TOUR_TRASH_TRANSLATION } from '../../../constants/define/translation';
import { TourTrashForm, TourTrashType } from '../../../types/tourtrash';
import { toPayloadTourTrash } from '../../../utils/transfer/tour-trash';
import { logging } from '../../../utils/log';
import { appStyles } from '../../../styles';

const { TabPane } = Tabs;

interface TourTrashModalProps {
  isOpenModal: boolean;
  onClose: () => void;
  isDisabledDetailTab: boolean;
  isDisabledUserTab: boolean;
  onRefresh: () => void;
  recordEdit?: TourTrashType;
  onUpdateEditingRecord: (tourtrash: TourTrashType) => void;
  isHandleViewTourTrash?: boolean;
  isEditTourTrashActive?: boolean;
  countrySelected: string;
}

export const TourTrashModal = (props: TourTrashModalProps) => {
  const { t } = useTranslation(TOUR_TRASH_TRANSLATION);
  const {
    isOpenModal,
    isDisabledDetailTab,
    isDisabledUserTab,
    onClose,
    onRefresh,
    recordEdit,
    isHandleViewTourTrash,
    isEditTourTrashActive,
    countrySelected,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [activeKey, setActiveKey] = useState('1');

  const onCreateTourTrash = useCallback(
    async (values: TourTrashForm, resetFunc: () => void) => {
      try {
        setIsLoading(true);
        const data = toPayloadTourTrash(values);
        await postCreateOrUpdateTourTrash(data);
        onClose();
        setTimeout(() => {
          if (values.tourTrashId) {
            message.success(t('message.tourTrash.update'));
          } else {
            message.success(t('message.tourTrash.create'));
          }
          resetFunc();
          onRefresh();
        }, 100);
      } catch (error: any) {
        logging(error.errorMessage);
        if (error.errorMessage && Array.isArray(error.errorMessage)) {
          message.error(error.errorMessage[0]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onClose, onRefresh],
  );

  const onChangeActiveKey = useCallback((key: string) => {
    setActiveKey(key);
  }, []);

  const onLoading = useCallback((isLoad: boolean) => {
    setIsLoading(isLoad);
  }, []);

  useEffect(() => {
    setActiveKey(isHandleViewTourTrash ? '2' : '1');
  }, [isHandleViewTourTrash]);

  return (
    <StyledModal
      title={t('title.tourTrash')}
      centered
      visible={isOpenModal}
      width={1024}
      footer={null}
      onCancel={onClose}
      maskClosable={false}
      closeIcon={<CloseCircleOutlined />}
    >
      <SpinLoading isLoading={isLoading} absolute>
        <Tabs activeKey={activeKey} className="tabs-tourtrash" onChange={onChangeActiveKey}>
          <TabPane tab={t('tab.detail.title')} key="1" className="tab-pane" disabled={isDisabledDetailTab}>
            <DetailsTab
              {...{
                onCreateTourTrash,
                recordEdit,
                isHandleViewTourTrash,
                isEditTourTrashActive,
                onLoading,
                countrySelected,
              }}
            />
          </TabPane>
          <TabPane tab={t('tab.player.title')} key="2" className="tab-pane" disabled={isDisabledUserTab || !recordEdit}>
            <UsersTab
              onUpdateEditingRecord={props.onUpdateEditingRecord}
              key={recordEdit?.id}
              onRefresh={onRefresh}
              tourTrash={recordEdit!}
              disableTab={isDisabledUserTab}
              onClose={onClose}
            />
          </TabPane>
        </Tabs>
      </SpinLoading>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  & > .ant-modal-content {
    ${appStyles.commonModalContentStyle}
    & .ant-tabs-nav-list {
      width: 99%;
      border-top: 1px solid #f0f0f0;
    }
    & .ant-tabs-tab {
      width: 50%;
      display: block;
      padding: 0.75rem;
      margin: 0;
      text-align: center;
    }
  }

  & .ant-tabs-tab-active {
    border-bottom: 3px solid #000;
    z-index: 1;
  }
`;
